/**
 * Camera Trajectory Overlay
 * 
 * Draws lines connecting consecutive camera centers in world space.
 * Must be rendered inside SimpleZoomPan to use the same coordinate system as frames.
 * Shows the camera motion path through the scene as simple line segments.
 * 
 * 🎨 TWO VISUAL MODES:
 *    - Debug mode (enabled=true): Green circles, yellow lines, frame numbers - always visible
 *    - Production mode (enabled=false): Subtle blurred dots, no lines/numbers - three-state visibility
 * 
 * 🔄 THREE-STATE VISIBILITY (Production Mode):
 *    1. Hidden (opacity: 0) - After 3s of inactivity, completely invisible for clean viewing
 *    2. Faded (opacity: 0.4) - During pan/zoom interaction, subtle presence
 *    3. Visible (opacity: 1) - When controls explicitly shown (tap/click)
 * 
 * 🎬 Single click/tap on a circle triggers animated navigation
 *    from current frame to clicked frame, blocking all input during animation.
 * 🔍 Double-click/double-tap opens balloon info (debug mode).
 */

import { useMemo, useState, useRef, useEffect } from 'react'
import { useAppStore } from '@/store/appStore'
import { calculateFrameSequenceTiming, getAnimationConfig } from '@/config/animationConfig'
import { getDoubleClickAnimationService } from '@/services/frameAnimationService'
import { OUTSIDE_FRAME_ZONE } from '@/constants/outsideFrameZone'

interface CameraTrajectoryOverlayProps {
  /** When true, show debug style (green circles, lines, numbers). When false, show production style. */
  enabled?: boolean
  lineColor?: string
  lineWidth?: number
  lineOpacity?: number
  onCircleClick?: (frameIndex: number | null) => void
  onClickedFrameChange?: (frameIndex: number | null) => void
  onBalloonSelect?: (frameIndex: number) => void
  highlightedFrameIndices?: Set<number>
  candidateFrameIndices?: Set<number>
}

// Production mode styles (matching app button style - frosted glass look)
const PRODUCTION_STYLES = {
  // Dots: frosted glass circles
  dotRadius: 12,
  dotFill: 'rgba(255, 255, 255, 0.15)',        // Semi-transparent white fill
  dotStroke: 'rgba(255, 255, 255, 0.4)',       // White border
  dotStrokeWidth: 1.5,
  
  // Selected dot: more prominent
  selectedDotFill: 'rgba(255, 255, 255, 0.25)',
  selectedDotStroke: 'rgba(255, 255, 255, 0.6)',
  selectedRingStroke: 'rgba(255, 255, 255, 0.5)',
  selectedRingWidth: 2,
  selectedRingRadius: 18,
  
  // Connecting line: same frosted white style as dots
  lineColor: 'rgba(255, 255, 255, 0.3)',       // Matching white
  lineWidth: 1.5,                              // Thin like dot border
  lineOpacity: 1,
  
  // Opacity states
  opacityVisible: 1,
  opacityFaded: 0.4,
}

// Debug mode styles (current bright colors)
const DEBUG_STYLES = {
  dotRadius: 10,
  normalDotFill: 'rgba(0, 255, 0, 1)', // Green
  selectedDotFill: 'rgba(0, 0, 255, 1)', // Blue
  clickedDotFill: 'rgba(0, 0, 0, 1)', // Black
}

/**
 * Convert quaternion to 3x3 rotation matrix (camera-to-world)
 * COLMAP stores rotation as [w, x, y, z] in Hamilton convention
 */
function quaternionToMatrix(q: [number, number, number, number]): number[][] {
  const [w, x, y, z] = q
  
  return [
    [1 - 2*(y*y + z*z), 2*(x*y - z*w), 2*(x*z + y*w)],
    [2*(x*y + z*w), 1 - 2*(x*x + z*z), 2*(y*z - x*w)],
    [2*(x*z - y*w), 2*(y*z + x*w), 1 - 2*(x*x + y*y)]
  ]
}

/**
 * Multiply 3x3 matrix by 3D vector
 */
function matrixVecMultiply(matrix: number[][], vec: [number, number, number]): [number, number, number] {
  return [
    matrix[0][0] * vec[0] + matrix[0][1] * vec[1] + matrix[0][2] * vec[2],
    matrix[1][0] * vec[0] + matrix[1][1] * vec[1] + matrix[1][2] * vec[2],
    matrix[2][0] * vec[0] + matrix[2][1] * vec[1] + matrix[2][2] * vec[2]
  ]
}

/**
 * Extract camera viewing direction from quaternion rotation using proper 3D math
 * Camera forward is (0, 0, -1) in camera space (OpenGL/COLMAP convention)
 * Transform to world space and project to 2D angle for trajectory overlay
 * Returns angle in radians (0 = right, π/2 = down, π = left, 3π/2 = up)
 */
function getCameraViewDirection(rotation: [number, number, number, number]): number {
  // Get rotation matrix from quaternion
  const R = quaternionToMatrix(rotation)
  
  // Camera forward vector in camera space (looking down -Z axis)
  const forwardCameraSpace: [number, number, number] = [0, 0, -1]
  
  // Transform to world space using rotation matrix
  const forwardWorld = matrixVecMultiply(R, forwardCameraSpace)
  
  // Project 3D world vector to 2D angle on trajectory overlay (ignores Z component)
  // atan2(y, x) gives angle from positive X axis
  const angle2D = Math.atan2(forwardWorld[1], forwardWorld[0])
  
  return angle2D
}

/**
 * Create clean SVG path for navigation hint arrows
 * Directional arrows: N/S/E/W (simple lines with arrowheads)
 * Z-arrows: forward/backward (converging/diverging for depth effect)
 */
export function createArrowPath(direction: 'north' | 'south' | 'east' | 'west' | 'forward' | 'backward'): string {
  switch (direction) {
    // N/S/E/W: Simple lines with arrowheads (24x24 viewBox)
    case 'north':
      // Up arrow: line from 12,20 to 12,4 with arrowhead
      return 'M12 20 L12 4 M8 8 L12 4 L16 8'
    case 'south':
      // Down arrow: line from 12,4 to 12,20 with arrowhead
      return 'M12 4 L12 20 M8 16 L12 20 L16 16'
    case 'east':
      // Right arrow: line from 4,12 to 20,12 with arrowhead
      return 'M4 12 L20 12 M16 8 L20 12 L16 16'
    case 'west':
      // Left arrow: line from 20,12 to 4,12 with arrowhead
      return 'M20 12 L4 12 M8 8 L4 12 L8 16'
    case 'forward':
      // Converging arrow: shows movement toward viewer (perspective)
      return 'M6 20 L6 10 L18 10 L18 20 M12 10 L12 2 M8 6 L12 2 L16 6'
    case 'backward':
      // Diverging arrow: shows movement away from viewer (perspective)
      return 'M12 4 L12 12 M8 8 L12 12 L16 8 M6 14 L6 22 L18 22 L18 14'
  }
}

/**
 * Get CSS positioning for fixed arrow buttons on viewport
 */
export function getArrowPosition(direction: 'north' | 'south' | 'east' | 'west' | 'forward' | 'backward'): React.CSSProperties {
  const base: React.CSSProperties = {
    position: 'fixed',
    zIndex: 100001,
  }
  switch (direction) {
    case 'north':
      return { ...base, top: '50px', left: '50%', transform: 'translateX(-50%)' }
    case 'south':
      return { ...base, bottom: '30px', left: '50%', transform: 'translateX(-50%)' }
    case 'east':
      return { ...base, right: '30px', top: '50%', transform: 'translateY(-50%)' }
    case 'west':
      return { ...base, left: '30px', top: '50%', transform: 'translateY(-50%)' }
    case 'forward':
      return { ...base, bottom: '100px', right: '100px' }
    case 'backward':
      return { ...base, bottom: '100px', left: '100px' }
  }
}

/**
 * Create SVG path for a directional chevron/arrow
 * Points from circle center in the given direction
 */
function createChevronPath(cx: number, cy: number, radius: number, angle: number, size: number = 8): string {
  // Chevron base point (on circle edge)
  const baseX = cx + radius * Math.cos(angle)
  const baseY = cy + radius * Math.sin(angle)
  
  // Chevron tip (extends beyond circle)
  const tipX = baseX + size * Math.cos(angle)
  const tipY = baseY + size * Math.sin(angle)
  
  // Chevron wings (small triangle)
  const wingAngle = Math.PI / 6 // 30 degrees
  const wingLength = size * 0.6
  
  const leftWingX = tipX + wingLength * Math.cos(angle + Math.PI - wingAngle)
  const leftWingY = tipY + wingLength * Math.sin(angle + Math.PI - wingAngle)
  
  const rightWingX = tipX + wingLength * Math.cos(angle + Math.PI + wingAngle)
  const rightWingY = tipY + wingLength * Math.sin(angle + Math.PI + wingAngle)
  
  // Create arrow path: left wing -> tip -> right wing
  return `M ${leftWingX} ${leftWingY} L ${tipX} ${tipY} L ${rightWingX} ${rightWingY}`
}

export function CameraTrajectoryOverlay({
  enabled = true,
  lineColor = 'rgba(100, 200, 255, 0.6)',
  lineWidth = 2,
  lineOpacity = 0.6,
  onCircleClick,
  onClickedFrameChange,
  onBalloonSelect,
  highlightedFrameIndices = new Set(),
  candidateFrameIndices = new Set()
}: CameraTrajectoryOverlayProps) {
  
  const frameWorldPositions = useAppStore(state => state.frameWorldPositions)
  const showDebugOverlay = useAppStore(state => state.showDebugOverlay)
  const manuallySelectedFrameIndex = useAppStore(state => state.manuallySelectedFrameIndex)
  const currentCameraIndex = useAppStore(state => state.currentCameraIndex)  // 🔧 Use this (actual displayed frame) instead of manuallySelectedFrameIndex (can be stale)
  const showNavigationIndicators = useAppStore(state => state.showNavigationIndicators)
  const trajectoryOverlayVisible = useAppStore(state => state.trajectoryOverlayVisible)
  const cameras = useAppStore(state => state.cameras)
  const imageDimensions = useAppStore(state => state.imageDimensions)
  const highlightedZoneDirection = useAppStore(state => state.highlightedZoneDirection)
  
  // 🎬 Frame sequence animation state
  const isFrameSequenceAnimating = useAppStore(state => state.isFrameSequenceAnimating)
  
  // Determine visual mode: debug (enabled) or production (!enabled)
  const isDebugMode = enabled
  const isProductionMode = !enabled
  
  const [clickedCircleIndex, setClickedCircleIndex] = useState<number | null>(null)
  const [highlightedTrajectoryFrame, setHighlightedTrajectoryFrame] = useState<number | null>(null)
  const lastClickRef = useRef<{ frameIndex: number; time: number } | null>(null)
  const lastTouchRef = useRef<{ frameIndex: number; time: number } | null>(null)
  const highlightTimerRef = useRef<NodeJS.Timeout | null>(null)
  
  // Track touch start position to distinguish tap from pan
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)
  const PAN_THRESHOLD = 10 // pixels - if touch moves more than this, it's a pan not a tap
  
  // ⏱️ Debounce constant for two-click pattern (3 second window to confirm)
  const TWO_CLICK_TIMEOUT = 3000 // 3 seconds to click again
  
  // 🕒 Auto-hide timer DISABLED - user controls visibility via toggle button
  // No automatic hiding - let user decide when to show/hide trajectory
  
  // 🎬 Escape key handler - cancel frame sequence animation (works on all devices)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const appState = useAppStore.getState()
        if (appState.isFrameSequenceAnimating) {
          console.log(`🎬 [Escape] Cancelling frame sequence animation, jumping to current frame`)
          appState.cancelFrameSequenceAnimation()
        }
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // ⏱️ Cleanup highlight timer on unmount
  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current)
      }
    }
  }, [])

  // Create SVG path connecting frame image centers (both modes)
  const trajectoryPath = useMemo(() => {
    if (frameWorldPositions.length < 2) return ''

    let path = ''
    
    for (let i = 0; i < frameWorldPositions.length; i++) {
      const framePos = frameWorldPositions[i]
      
      // Frame center in world space
      const centerX = framePos.worldX
      const centerY = framePos.worldY
      
      if (i === 0) {
        path += `M ${centerX} ${centerY}`
      } else {
        path += ` L ${centerX} ${centerY}`
      }
    }

    return path
  }, [frameWorldPositions, isDebugMode])

  // Create markers at each trajectory point
  const trajectoryMarkers = useMemo(() => {
    if (frameWorldPositions.length < 1) return []

    const markerRadius = isDebugMode ? DEBUG_STYLES.dotRadius : PRODUCTION_STYLES.dotRadius
    
    return frameWorldPositions.map((framePos, idx) => {
      const cx = framePos.worldX
      const cy = framePos.worldY

      return {
        key: `marker-${idx}`,
        cx,
        cy,
        radius: markerRadius
      }
    })
  }, [frameWorldPositions, isDebugMode])

  // Don't render if no frame positions
  if (frameWorldPositions.length < 2) {
    return null
  }

  const handleCircleInteraction = (frameIndex: number, isTouch: boolean = false) => {
    const appState = useAppStore.getState()
    
    // 🎬 Cancel animation on any circle interaction
    if (appState.isFrameSequenceAnimating) {
      console.log(`[Circle] Cancelling animation on circle interaction`)
      appState.cancelFrameSequenceAnimation()
      return
    }
    
    const now = Date.now()
    const lastRef = isTouch ? lastTouchRef : lastClickRef
    const lastInteraction = lastRef.current
    
    // 🎯 TWO-CLICK PATTERN: First click highlights, second click animates
    // Check if this frame is already highlighted
    if (highlightedTrajectoryFrame === frameIndex && lastInteraction && lastInteraction.frameIndex === frameIndex) {
      // ✅ SECOND CLICK: Verify it's been long enough to confirm (>100ms debounce)
      if (now - lastInteraction.time < 100) {
        console.log(`🎯 [TwoClick-Select] Ignoring rapid re-click (debouncing < 100ms)`)
        return
      }
      
      // ✅ SECOND CLICK: Animate from current frame to clicked frame
      console.log(`🎯 [TwoClick-Confirm] Second click on frame ${frameIndex} - animating now`)
      const currentFrame = appState.currentCameraIndex  // 🔧 Use actual displayed frame, not potentially stale manuallySelectedFrameIndex
      const targetFrame = frameIndex
      
      // Clear highlight before animation
      setHighlightedTrajectoryFrame(null)
      lastRef.current = { frameIndex, time: now }
      
      // If clicking current frame or no current frame, just center it
      if (currentFrame === null || currentFrame === targetFrame) {
        console.log(`🎬 [TwoClick-Confirm] Frame ${targetFrame} is already current, just centering`)
        appState.setManuallySelectedFrame(targetFrame)
        appState.centerViewportOnFrame(targetFrame)
        return
      }
      
      // Calculate timing based on frame count
      const timing = calculateFrameSequenceTiming(currentFrame, targetFrame)
      const frameCount = Math.abs(targetFrame - currentFrame)
      
      console.log(`🎬 [TwoClick-Confirm] Animation: ${currentFrame} → ${targetFrame} (${frameCount} frames)`)
      console.log(`   Timing: ${timing.frameDuration}ms per frame, ${timing.framesToShow.length} frames shown, ~${timing.totalDuration}ms total`)
      
      // Set animation blocking state BEFORE starting animation
      appState.setFrameSequenceAnimating(true)
      appState.setFrameSequenceAnimationTarget(targetFrame)
      appState.setAnimatingFrameIndex(timing.framesToShow[0])
      appState.setAnimationTrigger('TWO_CLICK_ANIMATE')
      
      // Get animation service and config
      const animationService = getDoubleClickAnimationService()
      const config = getAnimationConfig('DOUBLE_CLICK_ANIMATE')
      
      if (config && timing.framesToShow.length > 0) {
        // Create a modified config with dynamic frame duration
        const dynamicConfig = {
          ...config,
          frameDuration: timing.frameDuration,
        }
        
        // Start the animation
        const cancelFn = animationService.animateFrameSequence(
          timing.framesToShow,
          targetFrame,
          dynamicConfig,
          {
            onFrameShow: (frameIdx: number) => {
              console.log(`🎬 [TwoClick-Confirm] Showing frame: ${frameIdx}`)
              const freshState = useAppStore.getState()
              freshState.setAnimatingFrameIndex(frameIdx)
              freshState.centerViewportOnFrame(frameIdx, undefined, undefined, { skipAutoSelect: true })
            },
            onComplete: (finalFrame: number) => {
              console.log(`✅ [TwoClick-Confirm] Animation complete, selecting frame: ${finalFrame}`)
              const freshState = useAppStore.getState()
              
              // Clear animation state
              freshState.setAnimatingFrameIndex(null)
              freshState.setAnimationTrigger(null)
              freshState.setFrameSequenceAnimating(false)
              freshState.setFrameSequenceAnimationCancel(null)
              freshState.setFrameSequenceAnimationTarget(null)
              
              // Select the final frame and center it
              freshState.setManuallySelectedFrame(finalFrame)
              freshState.centerViewportOnFrame(finalFrame)
            }
          }
        )
        
        // Store cancel function for gestures/Escape key
        appState.setFrameSequenceAnimationCancel(cancelFn)
      } else {
        // Fallback: just jump directly
        console.warn(`🎬 [TwoClick-Confirm] No animation config, jumping directly`)
        appState.setFrameSequenceAnimating(false)
        appState.setFrameSequenceAnimationTarget(null)
        appState.setAnimatingFrameIndex(null)
        appState.setAnimationTrigger(null)
        appState.setManuallySelectedFrame(targetFrame)
        appState.centerViewportOnFrame(targetFrame)
      }
      return
    } else {
      // 🎯 FIRST CLICK: Highlight the frame (no animation yet)
      console.log(`🎯 [TwoClick-Select] Highlighting trajectory frame ${frameIndex} - click again to animate`)
      
      // Clear any existing highlight timer
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current)
      }
      
      // Set highlight and auto-clear after timeout if not confirmed
      setHighlightedTrajectoryFrame(frameIndex)
      lastRef.current = { frameIndex, time: now }
      
      // Auto-clear highlight after 1 second if not confirmed with second click
      highlightTimerRef.current = setTimeout(() => {
        console.log(`🎯 [TwoClick-Select] Highlight timeout - clearing ${frameIndex}`)
        setHighlightedTrajectoryFrame(null)
        highlightTimerRef.current = null
      }, TWO_CLICK_TIMEOUT)
      
      return
    }
    
    // Check if this is a double-tap/double-click (same frame, within 300ms) - DISABLED FOR TWO-CLICK PATTERN
    if (lastInteraction && lastInteraction.frameIndex === frameIndex && now - lastInteraction.time < 300) {
      // 🔍 DOUBLE-CLICK: Show balloon info (secondary action)
      if (clickedCircleIndex === frameIndex) {
        // Already showing balloon, close it
        if (showDebugOverlay) {
          onBalloonSelect?.(frameIndex)
        }
        onCircleClick?.(null)
        onClickedFrameChange?.(null)
        setClickedCircleIndex(null)
      } else {
        // Show balloon
        onCircleClick?.(frameIndex)
        onClickedFrameChange?.(frameIndex)
        if (showDebugOverlay) {
          onBalloonSelect?.(frameIndex)
        }
        setClickedCircleIndex(frameIndex)
      }
      
      lastRef.current = null
      return
    }
  }

  // Simple on/off visibility controlled by toggle button
  // Works in both production and debug modes
  const productionOpacity = trajectoryOverlayVisible ? 1 : 0

  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 0.9;
            r: ${trajectoryMarkers.length > 0 ? trajectoryMarkers[0].radius + 10 : 20}px;
          }
          50% {
            opacity: 0.5;
            r: ${trajectoryMarkers.length > 0 ? trajectoryMarkers[0].radius + 16 : 26}px;
          }
        }
      `}</style>
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '10000px',
          height: '10000px',
          pointerEvents: 'none',
          zIndex: 50000,  // Above max frame z-index (40000 for selected/hovered frames)
          overflow: 'visible',
          touchAction: 'none', // Prevent default touch behaviors (scrolling, zooming)
          opacity: productionOpacity,
          transition: 'opacity 0.3s ease'  // Smooth fade transition
        }}
      >
      {/* Main trajectory line - both modes with different colors */}
      <path
        d={trajectoryPath}
        stroke={isDebugMode ? lineColor : PRODUCTION_STYLES.lineColor}
        strokeWidth={isDebugMode ? lineWidth : PRODUCTION_STYLES.lineWidth}
        fill="none"
        opacity={isDebugMode ? lineOpacity : PRODUCTION_STYLES.lineOpacity}
        strokeLinecap="round"
        strokeLinejoin="round"
        pointerEvents="none"
      />



      {/* 🎯 OUTSIDE-FRAME SELECTION ZONE - yellow rectangle at 80%-300% distance + ±50% perpendicular
      {highlightedZoneDirection && manuallySelectedFrameIndex !== null && frameWorldPositions[manuallySelectedFrameIndex] && imageDimensions && (() => {
        const sf = frameWorldPositions[manuallySelectedFrameIndex]
        const halfWidth = imageDimensions.displayWidth / 2
        const halfHeight = imageDimensions.displayHeight / 2

        // Distance band: minDistancePercent to maxDistancePercent of dimension
        // Perpendicular: ±perpendularAlignmentPercent of perpendicular dimension
        const minDist = OUTSIDE_FRAME_ZONE.minDistancePercent
        const maxDist = OUTSIDE_FRAME_ZONE.maxDistancePercent
        const perpAlign = OUTSIDE_FRAME_ZONE.perpendularAlignmentPercent

        let rectX = 0, rectY = 0, rectWidth = 0, rectHeight = 0

        switch (highlightedZoneDirection) {
          case 'east':
            // Right: x from +minDist to +maxDist, y from -perpAlign to +perpAlign
            rectX = sf.worldX + halfWidth * minDist
            rectY = sf.worldY - halfHeight * perpAlign
            rectWidth = halfWidth * (maxDist - minDist)
            rectHeight = halfHeight * 2 * perpAlign
            break
          case 'west':
            // Left: x from -maxDist to -minDist, y from -perpAlign to +perpAlign
            rectX = sf.worldX - halfWidth * maxDist
            rectY = sf.worldY - halfHeight * perpAlign
            rectWidth = halfWidth * (maxDist - minDist)
            rectHeight = halfHeight * 2 * perpAlign
            break
          case 'north':
            // Up: y from -maxDist to -minDist, x from -perpAlign to +perpAlign
            rectX = sf.worldX - halfWidth * perpAlign
            rectY = sf.worldY - halfHeight * maxDist
            rectWidth = halfWidth * 2 * perpAlign
            rectHeight = halfHeight * (maxDist - minDist)
            break
          case 'south':
            // Down: y from +minDist to +maxDist, x from -perpAlign to +perpAlign
            rectX = sf.worldX - halfWidth * perpAlign
            rectY = sf.worldY + halfHeight * minDist
            rectWidth = halfWidth * 2 * perpAlign
            rectHeight = halfHeight * (maxDist - minDist)
            break
        }

        return (
          <g key="zone-rectangle" pointerEvents="none">
            <rect
              x={rectX}
              y={rectY}
              width={rectWidth}
              height={rectHeight}
              fill="rgba(255, 200, 0, 0.15)"
              stroke="rgba(255, 200, 0, 0.8)"
              strokeWidth={2}
              opacity={0.7}
            />
          </g>
        )
      })()} */}
      
      {/* Markers at each trajectory point */}
      {trajectoryMarkers.map((marker, idx) => {
        const isHighlighted = highlightedFrameIndices.has(idx)
        const isCandidate = candidateFrameIndices.has(idx)
        const isSelected = idx === manuallySelectedFrameIndex
        const isClicked = idx === clickedCircleIndex
        const isTrajectoryHighlighted = idx === highlightedTrajectoryFrame
        
        // Only use larger touch target on actual touch devices, not on desktop
        const isTouchDevice = () => {
          return (('ontouchstart' in window) ||
                  ((navigator as any).maxTouchPoints > 0) ||
                  ((navigator as any).msMaxTouchPoints > 0))
        }
        const touchTargetRadius = isTouchDevice() ? marker.radius * 3 : marker.radius
        
        // Determine dot fill color based on mode
        const getDotFill = () => {
          // Candidate frames get golden color (both modes)
          // if (isCandidate) return 'rgba(255, 200, 0, 0.8)'
          
          if (isDebugMode) {
            if (isClicked) return DEBUG_STYLES.clickedDotFill
            if (isSelected) return DEBUG_STYLES.selectedDotFill
            return DEBUG_STYLES.normalDotFill
          } else {
            // Production mode
            if (isSelected) return PRODUCTION_STYLES.selectedDotFill
            return PRODUCTION_STYLES.dotFill
          }
        }
        
        return (
          <g
            key={marker.key}
            style={{ 
              cursor: isFrameSequenceAnimating ? 'not-allowed' : 'pointer',
              opacity: isFrameSequenceAnimating ? 0.3 : 1,
              transition: 'opacity 0.3s ease'
            }}
            onClick={(e) => {
              e.stopPropagation()
              handleCircleInteraction(idx, false)
            }}
            pointerEvents={
              isProductionMode && !trajectoryOverlayVisible 
                ? "none"  // Hidden state - disable clicks
                : "auto"  // Visible/Faded/Debug - allow clicks
            }
          >
            {/* Invisible touch target - sized appropriately for device type */}
            <circle
              cx={marker.cx}
              cy={marker.cy}
              r={touchTargetRadius}
              fill="transparent"
              pointerEvents={
                isProductionMode && !trajectoryOverlayVisible 
                  ? "none"  // Hidden state - disable clicks
                  : "auto"  // Visible/Faded/Debug - allow clicks
              }
              onClick={(e) => {
                e.stopPropagation()
                handleCircleInteraction(idx, false)
              }}
              onTouchStart={(e) => {
                // Cancel animation on any touch
                const appState = useAppStore.getState()
                if (appState.isFrameSequenceAnimating) {
                  console.log(`🎬 [TouchStart] Cancelling animation`)
                  appState.cancelFrameSequenceAnimation()
                  e.preventDefault()
                  e.stopPropagation()
                  return
                }
                
                // Record touch start position to detect pan vs tap
                if (e.touches.length === 1) {
                  touchStartRef.current = {
                    x: e.touches[0].clientX,
                    y: e.touches[0].clientY
                  }
                }
              }}
              onTouchEnd={(e) => {
                e.preventDefault()
                e.stopPropagation()
                
                // Check if this was a pan (moved too far) or a tap
                if (touchStartRef.current && e.changedTouches.length === 1) {
                  const endX = e.changedTouches[0].clientX
                  const endY = e.changedTouches[0].clientY
                  const dx = endX - touchStartRef.current.x
                  const dy = endY - touchStartRef.current.y
                  const distance = Math.sqrt(dx * dx + dy * dy)
                  
                  if (distance > PAN_THRESHOLD) {
                    // This was a pan gesture, not a tap - ignore
                    touchStartRef.current = null
                    return
                  }
                }
                
                touchStartRef.current = null
                handleCircleInteraction(idx, true)
              }}
            />
            
            {/* Trajectory highlight ring (pulsing glow) - shown when this frame is selected for animation */}
            {isTrajectoryHighlighted && (
              <circle
                cx={marker.cx}
                cy={marker.cy}
                r={marker.radius + 10}
                fill="none"
                stroke="rgba(100, 200, 255, 1)"
                strokeWidth={2}
                opacity={0.9}
                pointerEvents="none"
                style={{
                  filter: 'drop-shadow(0 0 8px rgba(100, 200, 255, 0.8))',
                  animation: 'pulse 1.5s ease-in-out infinite'
                }}
              />
            )}
            
            {/* Candidate ring (golden glow) - shown around candidate frames
            {isCandidate && (
              <circle
                cx={marker.cx}
                cy={marker.cy}
                r={marker.radius + 6}
                fill="none"
                stroke="rgba(255, 200, 0, 1)"
                strokeWidth={2}
                opacity={0.9}
                pointerEvents="none"
                style={{
                  filter: 'drop-shadow(0 0 4px rgba(255, 200, 0, 0.8))'
                }}
              />
            )} */}
            
            {/* Debug mode: Highlighted ring (golden glow) - shown when this camera sees the clicked point
            {isDebugMode && isHighlighted && (
              <circle
                cx={marker.cx}
                cy={marker.cy}
                r={marker.radius + 8}
                fill="none"
                stroke="rgba(255, 200, 0, 0.8)"
                strokeWidth={3}
                opacity={0.9}
                pointerEvents="none"
                style={{
                  filter: 'drop-shadow(0 0 6px rgba(255, 200, 0, 0.6))'
                }}
              />
            )} */}
            
            {/* Production mode: Selected frame ring */}
            {isProductionMode && isSelected && (
              <circle
                cx={marker.cx}
                cy={marker.cy}
                r={PRODUCTION_STYLES.selectedRingRadius}
                fill="none"
                stroke={PRODUCTION_STYLES.selectedRingStroke}
                strokeWidth={PRODUCTION_STYLES.selectedRingWidth}
                opacity={1}
                pointerEvents="none"
              />
            )}
            
            {/* Directional chevron - only visible when overlay is fully visible */}
            {showNavigationIndicators && isProductionMode && cameras[idx] && (() => {
              // Calculate angle relative to selected camera
              // Selected camera always points up (12 o'clock = -π/2)
              // Other cameras show their direction relative to selected camera (counter-clockwise)
              let chevronAngle: number
              
              if (isSelected) {
                // Selected camera: always point up (north, 12 o'clock)
                chevronAngle = -Math.PI / 2
              } else if (manuallySelectedFrameIndex !== null && cameras[manuallySelectedFrameIndex]) {
                // Other cameras: show direction relative to selected camera
                const selectedAngle = getCameraViewDirection(cameras[manuallySelectedFrameIndex].rotation)
                const thisAngle = getCameraViewDirection(cameras[idx].rotation)
                // Relative angle with counter-clockwise rotation: negate the difference
                // If other camera looks 90° clockwise from selected → shows pointing left (counter-clockwise from up)
                chevronAngle = -Math.PI / 2 - (thisAngle - selectedAngle)
              } else {
                // Fallback: no selected camera, use absolute direction
                chevronAngle = getCameraViewDirection(cameras[idx].rotation)
              }
              
              return (
                <path
                  d={createChevronPath(
                    marker.cx,
                    marker.cy,
                    marker.radius,
                    chevronAngle,
                    6 // Chevron size in pixels
                  )}
                  stroke={isSelected ? '#3b82f6' : '#94a3b8'} // Blue for selected, gray for others
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                  style={{ pointerEvents: 'none' }} // Never intercept clicks
                />
              )
            })()}
            
            {/* Visible circle marker */}
            <circle
              cx={marker.cx}
              cy={marker.cy}
              r={marker.radius}
              fill={getDotFill()}
              stroke={isProductionMode ? (isSelected ? PRODUCTION_STYLES.selectedDotStroke : PRODUCTION_STYLES.dotStroke) : undefined}
              strokeWidth={isProductionMode ? PRODUCTION_STYLES.dotStrokeWidth : undefined}
              opacity={1}
              pointerEvents="none"
            />
            
            {/* Frame index label - only in debug mode */}
            {isDebugMode && (
              <text
                x={marker.cx}
                y={marker.cy}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="white"
                fontSize="12"
                fontWeight="bold"
                pointerEvents="none"
              >
                {idx}
              </text>
            )}
          </g>
        )
      })}
    </svg>
    </>
  )
}
